// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css } from '@emotion/css';
import { Theme, Tooltip, TooltipProps, Typography, useTheme } from '@mui/material';

import Adornment, { AdornmentProps } from '@components/Timeline/Timeline/Adornment';
import Tag, { TagProps } from '@components/Timeline/Timeline/Tag';

// CONSTANTS ---------------------------------------------------------------- //

const borderWidth = 2;

// TYPES ---------------------------------------------------------------- //

export interface TileProps {
  adornments?: AdornmentProps[]; // Layered elements on top of the tile
  color?: string; // Main color of the tile (background for solid variant, border for outlined variant)
  extraWidthBefore?: number;
  extraWidthBeforeLabel?: string;
  extraWidthBeforeOpacity?: number;
  extraWidthBeforeTooltip?: React.ReactNode | string;
  extraWidthBeforeTooltipPlacement?: TooltipProps['placement'];
  extraWidthBetween?: number;
  extraWidthBetweenLabel?: string;
  extraWidthBetweenOpacity?: number;
  extraWidthBetweenTooltip?: React.ReactNode | string;
  extraWidthBetweenTooltipPlacement?: TooltipProps['placement'];
  extraWidthAfter?: number;
  extraWidthAfterLabel?: string;
  extraWidthAfterOpacity?: number;
  extraWidthAfterTooltip?: React.ReactNode | string;
  extraWidthAfterTooltipPlacement?: TooltipProps['placement'];
  identifier: string | number;
  maxWidth?: string | number;
  minWidth?: string | number;
  overlayColor?: string;
  overlayOpacity?: number;
  overlayText?: string;
  primaryText1?: string;
  primaryText2?: string;
  roundLeftCorners?: boolean;
  roundRightCorners?: boolean;
  secondaryIcon?: React.ReactNode;
  secondaryText?: string;
  size?: 'compressed' | 'small' | 'medium' | 'large';
  tags?: TagProps[];
  variant?: 'solid' | 'outlined' | 'placeholder-solid' | 'placeholder-outlined';
  width?: string | number;
  zIndex?: number;
  zoom?: number;
}

// COMPONENT ---------------------------------------------------------------- //

const Tile = ({
  adornments = [],
  color = '#404860',
  extraWidthBefore = 0,
  extraWidthBeforeLabel = 'PICKUP',
  extraWidthBeforeOpacity = 0.7,
  extraWidthBeforeTooltip,
  extraWidthBeforeTooltipPlacement = 'top',
  extraWidthBetween = 0,
  extraWidthBetweenLabel = 'DRIVE',
  extraWidthBetweenOpacity = 0.9,
  extraWidthBetweenTooltip,
  extraWidthBetweenTooltipPlacement = 'top',
  extraWidthAfter = 0,
  extraWidthAfterLabel = 'DELIVERY',
  extraWidthAfterOpacity = 0.7,
  extraWidthAfterTooltip,
  extraWidthAfterTooltipPlacement = 'top',
  identifier,
  maxWidth,
  minWidth,
  overlayColor = '#ffffff',
  overlayOpacity = 0,
  overlayText,
  primaryText1 = 'Pickup',
  primaryText2 = 'Delivery',
  roundLeftCorners = true,
  roundRightCorners = true,
  secondaryIcon,
  secondaryText = 'Customer',
  size = 'medium',
  tags = [],
  variant = 'solid',
  width,
  zIndex = 0,
  zoom = 4,
}: TileProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  /** Get the styles of the outer tile */
  const getOuterStyles = () => {
    let styles: React.CSSProperties = {};

    if (size === 'compressed') styles.height = '24px';
    else if (size === 'small') styles.height = '48px';
    else if (size === 'medium') styles.height = '64px';
    else if (size === 'large') styles.height = '72px';

    if (roundLeftCorners) {
      styles.borderTopLeftRadius = '8px';
      styles.borderBottomLeftRadius = '8px';
    }
    if (roundRightCorners) {
      styles.borderTopRightRadius = '8px';
      styles.borderBottomRightRadius = '8px';
    }

    styles.borderColor = color;

    return styles;
  };

  /** Get the styles of the inner tile */
  const getInnerStyles = () => {
    let styles: React.CSSProperties = {};

    if (width) {
      // Subtract 4px to account for the border width
      if (typeof width === 'number') styles.width = `${width * zoom - 4}px`;
      else styles.width = width;
    }
    if (minWidth) {
      // Subtract 4px to account for the border width
      if (typeof minWidth === 'number') styles.minWidth = `${minWidth * zoom - 4}px`;
      else styles.minWidth = minWidth;
    }
    if (maxWidth) {
      // Subtract 4px to account for the border width
      if (typeof maxWidth === 'number') styles.maxWidth = `${maxWidth * zoom - 4}px`;
      else styles.maxWidth = maxWidth;
    }

    if (size === 'compressed') styles.height = '20px';
    else if (size === 'small') styles.height = '44px';
    else if (size === 'medium') styles.height = '60px';
    else if (size === 'large') styles.height = '68px';

    if (variant === 'outlined') {
      styles.backgroundColor = '#ffffff';
      styles.color = color;
    } else {
      styles.backgroundColor = color;
      styles.color = '#ffffff';
    }

    return styles;
  };

  /** Get the styles of the edge fade */
  const getFadeStyles = () => {
    let styles: React.CSSProperties = {};

    styles.zIndex = zIndex + 1;

    if (size === 'compressed') styles.height = '20px';
    else if (size === 'small') styles.height = '44px';
    else if (size === 'medium') styles.height = '60px';
    else if (size === 'large') styles.height = '68px';

    if (variant === 'outlined') {
      styles.background = `linear-gradient(90deg, #ffffff00, #ffffff)`;
    } else {
      styles.background = `linear-gradient(90deg, ${color}00, ${color})`;
    }

    return styles;
  };

  /** Get the styles of the extra width before */
  const getExtraBeforeStyles = () => {
    let styles: React.CSSProperties = {};

    styles.backgroundColor = color;
    styles.opacity = extraWidthBeforeOpacity;

    if (extraWidthBefore > 0) styles.width = `${extraWidthBefore * zoom}px`;

    return styles;
  };

  /** Get the styles of the extra width between */
  const getExtraBetweenStyles = () => {
    let styles: React.CSSProperties = {};

    styles.backgroundColor = color;
    styles.opacity = extraWidthBetweenOpacity;

    if (extraWidthBetween > 0) styles.width = `${extraWidthBetween * zoom}px`;

    return styles;
  };

  /** Get the styles of the extra width after */
  const getExtraAfterStyles = () => {
    let styles: React.CSSProperties = {};

    styles.backgroundColor = color;
    styles.opacity = extraWidthAfterOpacity;

    if (extraWidthAfter > 0) styles.width = `${extraWidthAfter * zoom}px`;

    return styles;
  };

  /** Get the styles of the overlay */
  const getOverlayStyles = () => {
    let styles: React.CSSProperties = {};

    styles.zIndex = zIndex + 10;
    styles.backgroundColor = overlayColor;
    styles.opacity = overlayOpacity;

    if (roundLeftCorners) {
      styles.borderTopLeftRadius = '8px';
      styles.borderBottomLeftRadius = '8px';
    }
    if (roundRightCorners) {
      styles.borderTopRightRadius = '8px';
      styles.borderBottomRightRadius = '8px';
    }

    return styles;
  };

  /** Get the styles of the overlay box */
  const getOverlayBoxStyles = () => {
    let styles: React.CSSProperties = {};

    styles.zIndex = zIndex + 10;

    return styles;
  };

  /** Get the styles of the tags */
  const getTagsStyles = () => {
    let styles: React.CSSProperties = {};

    styles.zIndex = zIndex + 2;

    return styles;
  };

  /** Get the styles of the tags grid */
  const getTagsGridStyles = () => {
    let styles: React.CSSProperties = {};

    if (variant === 'outlined') {
      styles.backgroundColor = '#ffffff';
      styles.color = color;
    } else {
      styles.backgroundColor = color;
      styles.color = '#ffffff';
    }

    return styles;
  };

  /** Get the styles of the tags background fade */
  const getTagsFadeStyles = () => {
    let styles: React.CSSProperties = {};

    if (variant === 'outlined') {
      styles.background = `linear-gradient(90deg, #ffffff00, #ffffff)`;
    } else {
      styles.background = `linear-gradient(90deg, ${color}00, ${color})`;
    }

    return styles;
  };

  return (
    <div key={identifier} className={cls.root} style={{ zIndex }}>
      {/* OVERLAY */}
      {overlayOpacity > 0 ? (
        <>
          <div className={cls.overlay} style={getOverlayStyles()} />

          {overlayText ? (
            <div className={cls.overlayBox} style={getOverlayBoxStyles()}>
              <Typography sx={clsx.overlayText}>{overlayText}</Typography>
            </div>
          ) : null}
        </>
      ) : null}

      {/* TILE OUTER */}
      <div className={cls.tileOuter} style={getOuterStyles()}>
        {/* TILE EXTRA BEFORE */}
        {extraWidthBefore > 0 ? (
          <Tooltip title={extraWidthBeforeTooltip} placement={extraWidthBeforeTooltipPlacement}>
            <div className={cls.tileExtra} style={getExtraBeforeStyles()}>
              <div className={cls.tileExtraBox}>
                <Typography sx={clsx.tileExtraText}>{extraWidthBeforeLabel}</Typography>
              </div>
            </div>
          </Tooltip>
        ) : null}

        {/* TILE INNER */}
        <div className={cls.tileInner} style={getInnerStyles()}>
          {/* PRIMARY TEXT */}
          <div className={cls.primaryTextBox}>
            <Typography sx={clsx.primaryText}>
              {identifier ? <span className={cls.identifier}>{identifier}</span> : null}
              {primaryText1}
            </Typography>

            <div className={cls.primaryTextSpacer} />

            {primaryText1 && primaryText2 ? <Typography sx={clsx.primaryText}>↪ {primaryText2}</Typography> : null}
            {!primaryText1 && primaryText2 ? <Typography sx={clsx.primaryText}>{primaryText2}</Typography> : null}
          </div>

          {/* SECONDARY ICON/TEXT */}
          {secondaryIcon ? (
            <div className={cls.secondaryIconBox}>{secondaryIcon}</div>
          ) : (
            <div className={cls.secondaryTextBox}>
              <Typography sx={clsx.secondaryText}>{secondaryText}</Typography>
            </div>
          )}

          {/* TAGS */}
          {tags?.length > 0 ? (
            <div className={cls.tags} style={getTagsStyles()}>
              {/* TAGS FADE */}
              <div className={cls.tagsFade} style={getTagsFadeStyles()} />

              {/* TAGS GRID */}
              <div className={cls.tagsGrid} style={getTagsGridStyles()}>
                {tags?.map(tag => (
                  <Tag {...tag} />
                ))}
              </div>
            </div>
          ) : null}

          {/* TILE FADE */}
          <div className={cls.tileFade} style={getFadeStyles()} />
        </div>

        {/* TILE EXTRA BETWEEN */}
        {extraWidthBetween > 0 ? (
          <Tooltip title={extraWidthBetweenTooltip} placement={extraWidthBetweenTooltipPlacement}>
            <div className={cls.tileExtra} style={getExtraBetweenStyles()}>
              <div className={cls.tileExtraBox}>
                <Typography sx={clsx.tileExtraText}>{extraWidthBetweenLabel}</Typography>
              </div>
            </div>
          </Tooltip>
        ) : null}

        {/* TILE EXTRA AFTER */}
        {extraWidthAfter > 0 ? (
          <Tooltip title={extraWidthAfterTooltip} placement={extraWidthAfterTooltipPlacement}>
            <div className={cls.tileExtra} style={getExtraAfterStyles()}>
              <div className={cls.tileExtraBox}>
                <Typography sx={clsx.tileExtraText}>{extraWidthAfterLabel}</Typography>
              </div>
            </div>
          </Tooltip>
        ) : null}
      </div>

      {/* ADORNMENTS */}
      {adornments?.length > 0 ? adornments?.map(adornment => <Adornment {...adornment} />) : null}
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  root: css`
    position: relative;
  `,

  overlay: css`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
  `,
  overlayBox: css`
    position: absolute;
    bottom: 6px;
    right: 6px;
  `,

  tileOuter: css`
    box-sizing: border-box;
    position: relative;
    overflow: hidden;
    display: flex;
    width: 100%;
    border-style: solid;
    border-width: ${borderWidth}px;
    background-color: #ffffff;
    cursor: pointer;
    transition: all 0.15s;
  `,
  tileInner: css`
    position: relative;
    overflow: hidden;
    width: 100%;
    padding: 6px;
  `,

  tileFade: css`
    position: absolute;
    top: 0;
    right: 0;
    width: 16px;
    pointer-events: none;
  `,

  tileExtra: css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background-image: repeating-linear-gradient(-45deg, transparent, transparent 10px, #ffffff40 10px, #ffffff40 20px);
    overflow: hidden;
  `,
  tileExtraBox: css`
    width: 60px;
    transform-origin: 0 0;
  `,

  primaryTextBox: css`
    position: absolute;
    top: 6px;
    left: 6px;
  `,
  primaryTextSpacer: css`
    width: 100%;
    height: 2px;
  `,

  identifier: css`
    margin-right: 4px;
    font-weight: 900;
  `,

  secondaryIconBox: css`
    position: absolute;
    bottom: 6px;
    left: 6px;
  `,
  secondaryTextBox: css`
    position: absolute;
    bottom: 6px;
    left: 6px;
  `,

  tags: css`
    position: absolute;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    bottom: 0;
    right: 0;
  `,
  tagsGrid: css`
    display: flex;
    align-items: flex-end;
    flex-wrap: nowrap;
    padding-bottom: 6px;
    padding-right: 6px;
  `,
  tagsFade: css`
    width: 16px;
    height: 24px;
    pointer-events: none;
  `,
});

const useSxStyles = (theme: Theme) => ({
  primaryText: {
    lineHeight: 1,
    fontSize: 12,
    fontWeight: 400,
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },

  secondaryText: {
    lineHeight: 1,
    fontSize: 12,
    fontWeight: 400,
    opacity: 0.7,
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },

  overlayText: {
    lineHeight: 1,
    textShadow: '-1px 1px 4px rgba(0,0,0,0.2)',
    fontWeight: 700,
    color: '#ffffff',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },

  tileExtraText: {
    lineHeight: 1,
    textAlign: 'center',
    textShadow: '-1px 1px 1px rgba(0,0,0,0.2)',
    fontSize: '9px',
    fontWeight: 900,
    color: theme.palette.common.white,
    opacity: 0.75,
    userSelect: 'none',
    whiteSpace: 'nowrap',
    transform: 'rotate(-90deg)',
  },
});

// EXPORT ---------------------------------------------------------------- //

export default Tile;
